import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FilterSearchInput } from "@/components/ui/filter-search-input";
import { filterBySearchTerms } from "@/lib/search-terms";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useMemo, useState } from "react";

export interface SearchableCheckboxItem {
  id: string;
  label: string;
  /** Optional secondary text shown next to the label and included in search. */
  description?: string;
}

interface SearchableCheckboxListProps {
  items: SearchableCheckboxItem[];
  selectedIds: string[];
  onToggle: (id: string, checked: boolean) => void;
  onClear: () => void;
  /** Text shown in the header, typically a selection count. */
  summary: string;
  clearLabel: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noItemsMessage?: string;
  className?: string;
}

/**
 * Search box above a scrollable checkbox list, matching the list column filter
 * dialogs used across the dashboards.
 */
export function SearchableCheckboxList({
  items,
  selectedIds,
  onToggle,
  onClear,
  summary,
  clearLabel,
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  noItemsMessage = "Nothing to select.",
  className,
}: SearchableCheckboxListProps) {
  const [searchValue, setSearchValue] = useState("");

  const filteredItems = useMemo(() => {
    const query = searchValue.trim();
    if (!query) return items;
    return filterBySearchTerms(
      items,
      query,
      (item) => `${item.label} ${item.description ?? ""}`,
    );
  }, [items, searchValue]);

  const hasSelection = selectedIds.length > 0;

  return (
    <div className={cn("rounded-md border", className)}>
      <Command shouldFilter={false}>
        <div className="flex items-center justify-between border-b pl-3.5 pr-2 py-2">
          <span className="text-sm font-medium text-muted-foreground">
            {summary}
          </span>
          {hasSelection && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onClear}
              className="h-6 w-6 p-0"
              aria-label={clearLabel}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        <FilterSearchInput
          placeholder={searchPlaceholder}
          value={searchValue}
          onValueChange={setSearchValue}
        />
        <CommandList className="max-h-64">
          {items.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {noItemsMessage}
            </div>
          ) : filteredItems.length > 0 ? (
            <CommandGroup>
              {filteredItems.map((item) => (
                <CommandItem
                  key={item.id}
                  className="flex items-center gap-2 text-xs"
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() =>
                    onToggle(item.id, !selectedIds.includes(item.id))
                  }
                >
                  <Checkbox
                    checked={selectedIds.includes(item.id)}
                    onCheckedChange={(checked) => onToggle(item.id, !!checked)}
                    onClick={(event) => event.stopPropagation()}
                  />
                  <span className="flex-1">{item.label}</span>
                  {item.description && (
                    <span className="text-muted-foreground">
                      {item.description}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          ) : (
            <CommandEmpty>{emptyMessage}</CommandEmpty>
          )}
        </CommandList>
        {hasSelection && (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="link"
              size="xs"
              onClick={onClear}
              className="w-full justify-start text-xs p-0 h-auto"
            >
              {clearLabel}
            </Button>
          </div>
        )}
      </Command>
    </div>
  );
}
